import { searchLocalIndex } from "@mirage/service-extension-tab-ingestion";
import { tagged } from "@mirage/service-logging";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import * as wrappers from "@mirage/service-typeahead-search/service/utils/wrappers";
import * as rx from "rxjs";

import { SearchResult } from "@mirage/service-dbx-api";
import * as primitives from "@mirage/service-typeahead-search/service/primitives";
import {
  filterRecommendations,
  tagAndInjectHitsFactory,
} from "@mirage/service-typeahead-search/service/utils/recommendation-helpers";
import {
  cancellableSourceFactory,
  getSourceLimit,
} from "@mirage/service-typeahead-search/service/utils/source-helpers";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import { getResultSection } from "../utils/section-mapper";

const logger = tagged("typeahead/extensions");

const FILTER_RESULT_LIMIT = 200;
const API_TIMEOUT_MS = 500;

const cancellableSource = cancellableSourceFactory<SearchResult>();

export const raw: typeahead.TaggedSearch = (query, mode, options) => {
  return rx.defer(() => {
    return cancellableSource(
      query,
      mode,
      options,
      async () =>
        await searchLocalIndex(query, {
          prefixSearch: true,
          skipHighlights: true,
        }),
      (allSearchResults) =>
        processSearchResultsToRecommendations(
          allSearchResults,
          query,
          mode,
          options,
        ),
      (reason, results) => {
        logger.debug(
          `Emitting ${results?.length ?? 0} results after ${reason}`,
        );
        if (!results) return [];
        return results;
      },
      {
        timeoutMs: API_TIMEOUT_MS,
      },
    );
  });
};

export function processSearchResultsToRecommendations(
  allSearchResults: SearchResult[],
  query: string,
  mode: typeahead.Mode,
  options: typeahead.TaggedSearchOptions,
) {
  const sourceLimit = getSourceLimit(
    typeahead.ResultType.Recommendation,
    query,
    mode,
    options.config,
    options.grossLimit,
    options.sectionLimit,
  );

  const sectionId = getResultSection(
    typeahead.ResultType.Recommendation,
    mode,
    query,
    options.config,
  );

  let recommendations = (allSearchResults || [])
    .slice(0, FILTER_RESULT_LIMIT)
    .map(
      (extensionResult) =>
        ({
          ...extensionResult,
          recordType: { ".tag": "browser_tab" },
          lastBrowserViewMs: extensionResult.updatedAtMs,
          providerUpdateAtMs: extensionResult.providerUpdateAtMs ?? undefined,
          clicks: null,
          lastClickedMs: null,
          browserViewsHistory: null,
        }) as Recommendation,
    );

  recommendations = filterRecommendations(
    recommendations,
    query,
    mode,
    options,
    {
      filterResultLimit: FILTER_RESULT_LIMIT,
    },
  );

  const tagAndInjectHits = tagAndInjectHitsFactory(
    primitives.recommendation,
    (result) => result.uuid,
    sourceLimit,
    sectionId,
  );

  return tagAndInjectHits(recommendations, query, mode, options);
}

export const search = wrappers.wrapped(SourceId.Recommendations, raw);
